import Drawer from "@via/components/drawer";
import OverflowTooltipContent from "@via/components/overflow-tooltip-content";
import SelectComponent from "@via/components/select/select";
import { isSingleSelectOption } from "@via/components/select/utils";
import { DrawerSize } from "@via/components/table/table";
import { classNames } from "@via/utils/helpers.tsx";
import {
  ArrowsUpDownIcon,
  Bars3Icon,
  ChartBarIcon,
  CircleStackIcon,
  EyeIcon,
  EyeSlashIcon,
  FunnelIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import {
  type FileQueryFilter,
  type FilterLogic,
  type MetadataFilter,
  type PipelineFilter,
} from "@via/pages/sample-trackers/samples/types/filters.ts";
import {
  formatFileQueryFilterForDisplay,
  formatFilterForDisplay,
  formatPipelineFilterForDisplay,
} from "@via/pages/sample-trackers/samples/utils/filter-helpers.ts";
import { type SampleExplorerView } from "common";
import { type DragEvent, type JSX, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import { type SelectOption } from "@via/utils/interfaces.tsx";

type ConfigureViewTab = "columns" | "chart" | "sort" | "data" | "filters";

interface SaveSampleExplorerViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedView: SampleExplorerView | null;
  initialLabel?: string;
  initialIsDefault?: boolean;
  formatRoute: string;
  formatRouteOptions: SelectOption[];
  isFormatRoutesLoading?: boolean;
  columnItems: Array<{ id: string; label: string }>;
  visibleFieldNames: string[];
  onToggleColumnVisibility: (fieldName: string, isVisible: boolean) => void;
  onReorderColumns: (orderedFieldNames: string[]) => void;
  onShowAllColumns: () => void;
  onHideAllColumns: () => void;
  sortFieldOptions: SelectOption[];
  selectedSortField: string;
  selectedSortDirection: "asc" | "desc";
  onSortFieldChange: (fieldName: string) => void;
  onSortDirectionChange: (direction: "asc" | "desc") => void;
  onFormatRouteChange: (formatRoute: string) => void;
  metadataFilters: MetadataFilter[];
  metadataLogic: FilterLogic;
  pipelineFilters: PipelineFilter[];
  pipelineLogic: FilterLogic;
  fileQueryFilters: FileQueryFilter[];
  fileQueryLogic: FilterLogic;
  isCreateLoading?: boolean;
  isUpdateLoading?: boolean;
  isDeleteLoading?: boolean;
  onSaveAsNew: (values: {
    label: string;
    isDefault: boolean;
    formatRoute?: string;
  }) => Promise<void>;
  onUpdate: (values: {
    label: string;
    isDefault: boolean;
    formatRoute?: string;
  }) => Promise<void>;
  onRequestDelete: () => void;
}

const tabClassName =
  "flex flex-1 flex-col items-center justify-center gap-2 rounded-xl px-3 py-4 text-sm font-medium transition";

const renderEmptyState = (message: string): JSX.Element => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
    {message}
  </div>
);

function FilterListSection({
  title,
  logic,
  items,
}: {
  title: string;
  logic: FilterLogic;
  items: string[];
}): JSX.Element {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-900">{title}</div>
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
            {logic}
          </div>
          <div className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
            {items.length}
            {items.length === 1 ? " filter" : " filters"}
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-2 text-sm text-gray-500">No filters configured.</div>
      ) : (
        <div className="mt-3 max-h-32 space-y-1 overflow-y-auto pr-1">
          {items.map((item, index) => (
            <div
              key={`${title}-${item}-${index}`}
              className="rounded-md bg-gray-50 px-2.5 py-2 text-sm text-gray-700">
              <OverflowTooltipContent
                tooltipId={`saved-view-filter-${title}-${index}`}
                tooltipText={item}
                enabled>
                <span>{item}</span>
              </OverflowTooltipContent>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SaveSampleExplorerViewModal({
  isOpen,
  onClose,
  selectedView,
  initialLabel = "",
  initialIsDefault = false,
  formatRoute,
  formatRouteOptions,
  isFormatRoutesLoading = false,
  columnItems,
  visibleFieldNames,
  onToggleColumnVisibility,
  onReorderColumns,
  onShowAllColumns,
  onHideAllColumns,
  sortFieldOptions,
  selectedSortField,
  selectedSortDirection,
  onSortFieldChange,
  onSortDirectionChange,
  onFormatRouteChange,
  metadataFilters,
  metadataLogic,
  pipelineFilters,
  pipelineLogic,
  fileQueryFilters,
  fileQueryLogic,
  isCreateLoading = false,
  isUpdateLoading = false,
  isDeleteLoading = false,
  onSaveAsNew,
  onUpdate,
  onRequestDelete,
}: SaveSampleExplorerViewModalProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<ConfigureViewTab>("columns");
  const [label, setLabel] = useState(initialLabel);
  const [isDefault, setIsDefault] = useState(initialIsDefault);
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLabel(initialLabel);
    setIsDefault(initialIsDefault);
    setActiveTab("columns");
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, [initialIsDefault, initialLabel, isOpen]);

  const trimmedLabel = label.trim();
  const isBusy = isCreateLoading || isUpdateLoading || isDeleteLoading;

  const formatOptions = useMemo<SelectOption[]>(() => {
    const baseOptions: SelectOption[] = [
      { label: "No preferred format", value: "" },
      ...formatRouteOptions,
    ];

    if (
      formatRoute &&
      !baseOptions.some(option => option.value === formatRoute)
    ) {
      baseOptions.push({
        label: `${formatRoute} (missing)`,
        value: formatRoute,
      });
    }

    return baseOptions;
  }, [formatRoute, formatRouteOptions]);

  const selectedFormatOption =
    formatOptions.find(option => option.value === formatRoute) ??
    formatOptions[0];

  const directionOptions: SelectOption[] = [
    { label: "Ascending", value: "asc" },
    { label: "Descending", value: "desc" },
  ];

  const visibleCount = visibleFieldNames.length;
  const filterSummaryCount =
    metadataFilters.length + pipelineFilters.length + fileQueryFilters.length;

  const handleColumnDrop = (targetFieldName: string): void => {
    if (!draggedColumnId || draggedColumnId === targetFieldName) {
      setDraggedColumnId(null);
      setDragOverColumnId(null);
      return;
    }

    const orderedFieldNames = columnItems.map(column => column.id);
    const sourceIndex = orderedFieldNames.indexOf(draggedColumnId);
    const targetIndex = orderedFieldNames.indexOf(targetFieldName);
    if (sourceIndex < 0 || targetIndex < 0) {
      setDraggedColumnId(null);
      setDragOverColumnId(null);
      return;
    }

    const nextOrderedFieldNames = [...orderedFieldNames];
    const [movedFieldName] = nextOrderedFieldNames.splice(sourceIndex, 1);
    nextOrderedFieldNames.splice(targetIndex, 0, movedFieldName);
    onReorderColumns(nextOrderedFieldNames);
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  };

  const handleColumnDragEnd = (): void => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  };

  const tabs: Array<{
    id: ConfigureViewTab;
    label: string;
    icon: JSX.Element;
    disabled?: boolean;
    tooltipText?: string;
  }> = [
    {
      id: "columns",
      label: "Columns",
      icon: <TableCellsIcon className="h-5 w-5" />,
    },
    {
      id: "filters",
      label: "Filters",
      icon: <FunnelIcon className="h-5 w-5" />,
    },
    {
      id: "sort",
      label: "Sort",
      icon: <ArrowsUpDownIcon className="h-5 w-5" />,
    },
    {
      id: "data",
      label: "Data",
      icon: <CircleStackIcon className="h-5 w-5" />,
    },
    {
      id: "chart",
      label: "Chart",
      icon: <ChartBarIcon className="h-5 w-5" />,
      disabled: true,
      tooltipText: "Chart settings are under development.",
    },
  ];

  return (
    <Drawer
      id="sample-explorer-view-settings"
      open={isOpen}
      setOpen={onClose}
      size={DrawerSize.Big}
      title="Configure View"
      resizable>
      <div className="flex h-full flex-col bg-gray-50">
        <div className="space-y-5 border-b border-gray-200 bg-white px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="text-sm text-gray-500">
                {selectedView ? (
                  <>
                    Editing:{" "}
                    <span className="font-medium text-gray-700">
                      {selectedView.label}
                    </span>
                  </>
                ) : (
                  "Configure the current explorer state and save it as a new view."
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {isDefault && (
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                    Default
                  </span>
                )}
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {visibleCount} columns visible
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {selectedSortField
                    ? `Sort: ${selectedSortField} ${selectedSortDirection}`
                    : "No default sort"}
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {formatRoute
                    ? `Format: ${formatRoute}`
                    : "No preferred format"}
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {filterSummaryCount} filters configured
                </span>
              </div>
            </div>
          </div>

          {selectedView?.warnings && selectedView.warnings.length > 0 && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
              {selectedView.warnings.map(warning => warning.message).join(" ")}
            </div>
          )}

          <div>
            <label
              htmlFor="sample-explorer-view-label"
              className="mb-2 block text-sm font-medium text-gray-900">
              View Name
            </label>
            <input
              id="sample-explorer-view-label"
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Quality Checks"
              maxLength={128}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex gap-2 rounded-2xl bg-gray-100 p-2">
            {tabs.map(tab => (
              <div
                key={tab.id}
                className="flex flex-1"
                data-tooltip-id={
                  tab.disabled ? `sample-explorer-tab-${tab.id}` : undefined
                }>
                <button
                  type="button"
                  disabled={tab.disabled}
                  onClick={() => setActiveTab(tab.id)}
                  className={classNames(
                    tabClassName,
                    "w-full",
                    tab.disabled
                      ? "cursor-not-allowed bg-gray-100 text-gray-400 opacity-70"
                      : activeTab === tab.id
                        ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                        : "text-gray-600 hover:bg-white/70"
                  )}>
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
                {tab.disabled && tab.tooltipText && (
                  <Tooltip
                    id={`sample-explorer-tab-${tab.id}`}
                    place="bottom"
                    content={tab.tooltipText}
                    className="z-10"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {activeTab === "columns" && (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    Column Order and Visibility
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    {visibleCount} of {columnItems.length} columns visible. Drag
                    rows to change their default order.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onShowAllColumns}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Show All
                  </button>
                  <button
                    type="button"
                    onClick={onHideAllColumns}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Hide All
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                {columnItems.map(column => {
                  const isVisible = visibleFieldNames.includes(column.id);
                  return (
                    <div
                      key={column.id}
                      draggable={!isBusy}
                      onDragStart={() => setDraggedColumnId(column.id)}
                      onDragOver={(event: DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        if (dragOverColumnId !== column.id) {
                          setDragOverColumnId(column.id);
                        }
                      }}
                      onDrop={() => handleColumnDrop(column.id)}
                      onDragEnd={handleColumnDragEnd}
                      className={classNames(
                        "flex items-center justify-between rounded-xl border bg-white px-3 py-2.5 transition-colors",
                        isBusy
                          ? "cursor-not-allowed"
                          : "cursor-grab active:cursor-grabbing",
                        dragOverColumnId === column.id
                          ? "border-indigo-300 ring-2 ring-indigo-100"
                          : "border-gray-200 hover:bg-gray-50"
                      )}>
                      <div className="flex items-center gap-3">
                        <div className="text-gray-400">
                          <Bars3Icon className="h-5 w-5" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {column.label}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          onToggleColumnVisibility(column.id, !isVisible)
                        }
                        className="rounded-full border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50">
                        {isVisible ? (
                          <EyeIcon className="h-4 w-4" />
                        ) : (
                          <EyeSlashIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === "chart" && (
            <div className="space-y-3">
              <div className="text-lg font-semibold text-gray-900">
                Chart Settings
              </div>
              {renderEmptyState(
                "No chart settings are configured for this view."
              )}
            </div>
          )}

          {activeTab === "sort" && (
            <div className="space-y-5">
              <div>
                <div className="text-lg font-semibold text-gray-900">
                  Sort Order
                </div>
                <div className="mt-1 text-sm text-gray-500">
                  Define the default sorting for this view. Only Sample Tracker
                  fields are supported here.
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900">
                    Field
                  </label>
                  <SelectComponent
                    name="sample-explorer-sort-field"
                    options={[
                      { label: "No default sort", value: "" },
                      ...sortFieldOptions,
                    ]}
                    value={
                      [
                        { label: "No default sort", value: "" },
                        ...sortFieldOptions,
                      ].find(option => option.value === selectedSortField) ?? {
                        label: "No default sort",
                        value: "",
                      }
                    }
                    placeholder="Select field"
                    onChange={selected => {
                      const nextValue = isSingleSelectOption(selected)
                        ? selected.value
                        : "";
                      onSortFieldChange(
                        typeof nextValue === "string" ? nextValue : ""
                      );
                    }}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-900">
                    Direction
                  </label>
                  <SelectComponent
                    name="sample-explorer-sort-direction"
                    options={directionOptions}
                    value={
                      directionOptions.find(
                        option => option.value === selectedSortDirection
                      ) ?? directionOptions[0]
                    }
                    placeholder="Select direction"
                    readOnly={!selectedSortField}
                    onChange={selected => {
                      const nextValue = isSingleSelectOption(selected)
                        ? selected.value
                        : "asc";
                      onSortDirectionChange(
                        nextValue === "desc" ? "desc" : "asc"
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "data" && (
            <div className="space-y-5">
              <div>
                <div className="text-lg font-semibold text-gray-900">Data</div>
                <div className="mt-1 text-sm text-gray-500">
                  Choose an API route to load sample explorer data with a
                  predefined metadata configuration. To join data from multiple
                  collections, define the route in Metadata Configuration &gt;
                  API Configs for the file collection.
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-900">
                  Preferred API route
                </label>
                <SelectComponent
                  name="sample-explorer-format-route"
                  options={formatOptions}
                  value={selectedFormatOption}
                  isLoading={isFormatRoutesLoading}
                  readOnly={isBusy}
                  placeholder="Select API route"
                  onChange={selected => {
                    const nextValue = isSingleSelectOption(selected)
                      ? selected.value
                      : "";
                    onFormatRouteChange(
                      typeof nextValue === "string" ? nextValue : ""
                    );
                  }}
                />
              </div>
            </div>
          )}

          {activeTab === "filters" && (
            <div className="space-y-3">
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
                These filters reflect the current explorer state and will be
                saved with the view.
              </div>

              <FilterListSection
                title="Metadata"
                logic={metadataLogic}
                items={metadataFilters.map(formatFilterForDisplay)}
              />
              <FilterListSection
                title="Run"
                logic={pipelineLogic}
                items={pipelineFilters.map(formatPipelineFilterForDisplay)}
              />
              <FilterListSection
                title="Reports"
                logic={fileQueryLogic}
                items={fileQueryFilters.map(formatFileQueryFilterForDisplay)}
              />
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 bg-white px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={e => setIsDefault(e.target.checked)}
                disabled={isBusy}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Set as default view
            </label>

            <div className="flex flex-wrap items-center justify-end gap-2">
              {selectedView && (
                <button
                  type="button"
                  onClick={onRequestDelete}
                  disabled={isBusy}
                  className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50">
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={isBusy}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void onSaveAsNew({
                    label: trimmedLabel,
                    isDefault,
                    formatRoute: formatRoute || undefined,
                  });
                }}
                disabled={!trimmedLabel || isBusy}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                Save As New
              </button>
              {selectedView && (
                <button
                  type="button"
                  onClick={() => {
                    void onUpdate({
                      label: trimmedLabel,
                      isDefault,
                      formatRoute: formatRoute || undefined,
                    });
                  }}
                  disabled={!trimmedLabel || isBusy}
                  className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-50">
                  Update
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
